/**
 * AuthSettingsForm - Authentication Settings Management
 * Allows configuring auth mode and login options
 */

import React, { useState, useEffect } from 'react';
import { getAuthSettings, updateAuthSettings } from './idpApi';

const AuthSettingsForm = ({ orgId }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [authMode, setAuthMode] = useState('legacy');
  const [legacyEnabled, setLegacyEnabled] = useState(true);
  const [idpEnabled, setIdpEnabled] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [orgId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAuthSettings(orgId);
      setSettings(data);
      setAuthMode(data.auth_mode || 'legacy');
      setLegacyEnabled(data.legacy_login_enabled !== false);
      setIdpEnabled(data.external_idp_enabled === true);
    } catch (e) {
      console.error('Failed to load auth settings:', e);
      // Settings might not exist yet, use defaults
      setSettings(null);
      setAuthMode('legacy');
      setLegacyEnabled(true);
      setIdpEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  // Auto-adjust checkboxes based on auth mode
  useEffect(() => {
    switch (authMode) {
      case 'legacy':
        setLegacyEnabled(true);
        setIdpEnabled(false);
        break;
      case 'external_idp':
        setLegacyEnabled(false);
        setIdpEnabled(true);
        break;
      case 'hybrid':
        setLegacyEnabled(true);
        setIdpEnabled(true);
        break;
      default:
        break;
    }
  }, [authMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const updated = await updateAuthSettings(orgId, {
        auth_mode: authMode,
        legacy_login_enabled: legacyEnabled,
        external_idp_enabled: idpEnabled,
      });

      setSettings(updated);
      setSuccess('Authentication settings updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      console.error('Failed to save settings:', e);
      setError('Failed to save authentication settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-settings-form">
      <h5 className="mB-20">Authentication Settings</h5>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card mB-20">
          <div className="card-header">
            <h6 className="mB-0">Authentication Mode</h6>
          </div>
          <div className="card-body">
            <div className="form-check mB-10">
              <input
                className="form-check-input"
                type="radio"
                name="authMode"
                id="authModeLegacy"
                value="legacy"
                checked={authMode === 'legacy'}
                onChange={() => setAuthMode('legacy')}
              />
              <label className="form-check-label" htmlFor="authModeLegacy">
                <strong>Legacy Only</strong>
                <p className="text-muted small mB-0">
                  Username/password authentication only
                </p>
              </label>
            </div>

            <div className="form-check mB-10">
              <input
                className="form-check-input"
                type="radio"
                name="authMode"
                id="authModeHybrid"
                value="hybrid"
                checked={authMode === 'hybrid'}
                onChange={() => setAuthMode('hybrid')}
              />
              <label className="form-check-label" htmlFor="authModeHybrid">
                <strong>Hybrid</strong>
                <span className="badge bg-primary ms-2">Recommended</span>
                <p className="text-muted small mB-0">
                  Both legacy and external IDP authentication (best for migration)
                </p>
              </label>
            </div>

            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="authMode"
                id="authModeIdp"
                value="external_idp"
                checked={authMode === 'external_idp'}
                onChange={() => setAuthMode('external_idp')}
              />
              <label className="form-check-label" htmlFor="authModeIdp">
                <strong>External IDP Only</strong>
                <p className="text-muted small mB-0">
                  External identity provider authentication only
                </p>
              </label>
            </div>
          </div>
        </div>

        <div className="card mB-20">
          <div className="card-header">
            <h6 className="mB-0">Login Options</h6>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="legacyEnabled"
                    checked={legacyEnabled}
                    onChange={(e) => setLegacyEnabled(e.target.checked)}
                    disabled={authMode !== 'hybrid'}
                  />
                  <label className="form-check-label" htmlFor="legacyEnabled">
                    Legacy Login Enabled
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="idpEnabled"
                    checked={idpEnabled}
                    onChange={(e) => setIdpEnabled(e.target.checked)}
                    disabled={authMode !== 'hybrid'}
                  />
                  <label className="form-check-label" htmlFor="idpEnabled">
                    External IDP Enabled
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {authMode === 'external_idp' && (
          <div className="alert alert-warning" role="alert">
            <i className="ti-alert mR-10"></i>
            <strong>Warning:</strong> Disabling legacy login will prevent users from logging in
            with username/password. Ensure all users are linked to an external identity before
            switching to External IDP Only mode.
          </div>
        )}

        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={loadSettings}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm mR-5" role="status"></span>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AuthSettingsForm;
